import dayjs from 'dayjs';
import _ from 'lodash';
import moment from 'moment';

import {
  ENUM_GENDER_JP_TEXT,
  ENUM_LANGUAGE_TEXT,
  ENUM_SURVEY_STATUS,
  ENUM_TYPE_PUBLIC_PERIOD_SETTING,
  ENUM_TYPE_REPORT_STATUS,
  ENUM_USER_ASSIGN_THERAPIST,
  ENUM_USER_STATUS,
} from 'constants/enum';
import { ENUM_PASSWORD_CREATE_ERROR } from 'constants/enum';
import { CATEGORY_VIDEOS_ASE, FORMAT_TIME } from 'constants/index';
import { TEXT_CREATE_SURVEY, TEXT_USER_LIST } from 'constants/string/texts';

const AthleticAbilityIcon_1 = require('assets/resources/AthleticAbilityIcon_1.svg').default;
const AthleticAbilityIcon_2 = require('assets/resources/AthleticAbilityIcon_2.svg').default;
const AthleticAbilityIcon_3 = require('assets/resources/AthleticAbilityIcon_3.svg').default;
const AthleticAbilityIcon_4 = require('assets/resources/AthleticAbilityIcon_4.svg').default;
const japanese_government_jp = require('data/japanese_government-jp.json');
const japanese_government_vi = require('data/japanese_government-vi.json');

/**
 * Handle eror
 */
export const handleError = (error) => {
  if (error.message) {
    console.warn(error.message);
    return;
  }

  console.warn(error);
  return;
};

/**
 * @name scrollToSection
 * Jump to the position with ID
 *
 * @param {string} id
 */
export const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    const rect = element.getBoundingClientRect();
    const isInView = rect.top >= 0 && rect.bottom <= window.innerHeight;

    if (!isInView) {
      element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }
};

/**
 * Scroll to position
 *
 * @param {number} position
 */
export const scrollToPosition = (position) => {
  window.scrollTo({
    top: position,
    behavior: 'smooth',
  });
};

/**
 * @name isApiResponseError
 *
 * @param {*} obj
 * @returns
 */
export const isApiResponseError = (obj) => {
  if (typeof obj === 'object' && obj !== null) {
    return 'error' in obj && 'status' in obj && 'statusText' in obj;
  }
  return false;
};

/**
 * @name validateEmail
 * @param {*} email
 * @returns {boolean}
 */
export const validateEmail = (email) => {
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

/**
 * @name checkPasswordRegister
 * Validate password register
 *
 * @param {*} password
 * @returns {Array.<ENUM_PASSWORD_CREATE_ERROR>}
 */
export const checkPasswordRegister = (password) => {
  const errors = [];

  if (password.length < 8) {
    errors.push(ENUM_PASSWORD_CREATE_ERROR.LENGTH);
  }

  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSymbols = /[!@#$%^&*()_+-]/.test(password);
  const fullWidth = isFullWidth(password);
  const isJapan = validateJapanese(password);

  if (!hasUpperCase) {
    errors.push(ENUM_PASSWORD_CREATE_ERROR.HAS_UPPERCASE);
  }
  if (!hasLowerCase) {
    errors.push(ENUM_PASSWORD_CREATE_ERROR.HAS_LOWERCASE);
  }
  if (!hasNumbers) {
    errors.push(ENUM_PASSWORD_CREATE_ERROR.HAS_NUMBER);
  }
  if (!hasSymbols) {
    errors.push(ENUM_PASSWORD_CREATE_ERROR.HAS_SYMBOLS);
  }
  if (fullWidth || isJapan) {
    errors.push(ENUM_PASSWORD_CREATE_ERROR.HAS_FULL_WIDTH);
  }

  let passClone = password;
  passClone = removeFullWidth(passClone);
  passClone = removeJapanese(passClone);
  const hasNoInvalidCharacters = /[^ !@#$%^&*()_+-\w]/.test(passClone);
  if (hasNoInvalidCharacters) {
    errors.push(ENUM_PASSWORD_CREATE_ERROR.HAS_SYMBOLS_REQUIRED);
  }

  return errors;
};

/**
 * Converts an object into a URL query string.
 *
 * @param {Object} obj
 * @returns {string}
 *
 * @example
 * // Returns '?sort=title&sort=-update_at&keyword=search'
 * convertToQueryString({ sort: ['title', '-updated_at'], keyword: 'search' });
 */
export const convertToQueryString = (obj) => {
  const queryString = Object.keys(obj)
    .filter((key) => obj[key] !== undefined && obj[key] !== null)
    .flatMap((key) => {
      const value = obj[key];
      if (Array.isArray(value)) {
        return value.map((item) => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .join('&');

  return queryString ? `?${queryString}` : '';
};

/**
 * check typeof value
 *
 * @param {*} value
 * @param {*} type
 * @returns {boolean}
 */
export const isType = (value, type) => {
  return typeof value === type;
};

/**
 * @name convertStatusSurvey
 * Description placeholder
 *
 * @param {*} status
 * @returns {("N/A" | { name: any; class: string; })}
 */
export const convertStatusSurvey = (status, t = null) => {
  if (!t) {
    return {
      name: '',
      class: '',
    };
  }
  if (status === ENUM_SURVEY_STATUS.PUBLIC) {
    return {
      name: t('PUBLIC_TEXT'),
      class: 'text-primary-346 border-primary-346/1 bg-primary-346/7',
    };
  }
  if (status === ENUM_SURVEY_STATUS.DRAFT) {
    return {
      name: t('DRAFT_TEXT'),
      class: 'text-logo border-gray-651 bg-white-750',
    };
  }
  if (status === ENUM_SURVEY_STATUS.PRIVATE) {
    return {
      name: t('PRIVATE_TEXT'),
      class: 'text-danger-229 border-danger-576 bg-danger-736',
    };
  }
  return 'N/A';
};

/**
 * @name convertStatusUserAssignTherapist
 * Description placeholder
 *
 * @param {*} status
 * @returns {("N/A" | { name: any; class: string; })}
 */
export const convertStatusUserAssignTherapist = (status, t = null) => {
  if (!t) {
    return {
      name: '',
      class: '',
    };
  }

  if (status === ENUM_USER_ASSIGN_THERAPIST.ASSIGNED) {
    return {
      name: t('ASSIGNED_THERAPIST'),
      class: 'text-primary-346 border-primary-346/1 bg-primary-346/7',
    };
  }
  if (status === ENUM_USER_ASSIGN_THERAPIST.NOT_ASSIGNED) {
    return {
      name: t('NOT_ASSIGNED_THERAPIST'),
      class: 'text-danger-229 border-danger-576 bg-danger-736',
    };
  }
};

/**
 * Convert time public
 *
 * @param {*} status
 * @param {*} start_time
 * @param {*} end_time
 */
export const convertTimePublic = (status, start_time, end_time) => {
  const _start_time = moment(start_time).format('YYYY/MM/DD HH:mm');
  const _end_time = moment(end_time).format('YYYY/MM/DD HH:mm');

  if (status === ENUM_SURVEY_STATUS.PUBLIC && end_time) {
    return TEXT_CREATE_SURVEY.PUBLIC_SETTING_END_TIME.replace('{end_time}', _end_time);
  }
  if (status === ENUM_SURVEY_STATUS.DRAFT) {
    if (start_time && end_time) {
      return TEXT_CREATE_SURVEY.DRAFT_PUBLIC_SETTING_PERIOD.replace('{start_time}', _start_time).replace('{end_time}', _end_time);
    } else if (start_time && !end_time) {
      return TEXT_CREATE_SURVEY.DRAFT_PUBLIC_SETTING_START_TIME.replace('{start_time}', _start_time);
    }
  }
};

/**
 * Check is show schedule text
 *
 * @param {*} status
 * @param {*} start_time
 * @param {*} end_time
 * @returns
 */
export const checkIsShowScheduleText = (status, start_time, end_time) => {
  if (status === ENUM_SURVEY_STATUS.PUBLIC) {
    return dayjs().isBefore(dayjs(end_time));
  }
  if (status === ENUM_SURVEY_STATUS.DRAFT) {
    return dayjs().isBefore(dayjs(start_time)) || dayjs().isAfter(dayjs(end_time));
  }
  return false;
};

/**
 * @name convertStatusUser
 * Description placeholder
 *
 * @param {*} status
 * @returns {("N/A" | { name: any; class: string; })}
 */
export const convertStatusUser = (status) => {
  if (status === ENUM_USER_STATUS.APPROVED) {
    return {
      name: TEXT_USER_LIST.APPROVED_TEXT,
      class: 'text-primary-346 border-primary-346/1 bg-primary-346/7',
    };
  }
  if (status === ENUM_USER_STATUS.NEW) {
    return {
      name: TEXT_USER_LIST.NEW_TEXT,
      class: 'text-logo border-gray-651 bg-white-750',
    };
  }
  if (status === ENUM_USER_STATUS.REJECTED) {
    return {
      name: TEXT_USER_LIST.REJECTED_TEXT,
      class: 'text-danger-229 border-danger-576 bg-danger-736',
    };
  }
  return 'N/A';
};

/**
 * @name convertStatusReport
 * Description placeholder
 *
 * @param {*} status
 * @returns {("N/A" | { name: any; class: string; })}
 */
export const convertStatusReport = (status) => {
  if (status === null) {
    return {
      name: '未作成',
      class: 'text-danger-229 border-danger-576 bg-danger-736',
    };
  } else if (status === ENUM_TYPE_REPORT_STATUS.FINISH) {
    return {
      name: '送信済',
      class: 'text-logo border-gray-651 bg-white-750',
    };
  } else {
    return {
      name: '作成中',
      class: 'text-primary-346 border-primary-346/1 bg-primary-346/7',
    };
  }
};

/**
 * @name fileToBase64
 *
 * @param {*} file
 * @returns
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    file
      .arrayBuffer()
      .then((buffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;

        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]);
        }

        const base64String = btoa(binary);
        resolve(`data:${file.type};base64,${base64String}`);
      })
      .catch(() => {
        reject(new Error('ファイルの読み取りに失敗しました。'));
      });
  });
};

/**
 * @name base64String
 *
 * @param {*} base64String
 * @param {*} filename
 * @returns
 */
export const base64ToFile = (base64String, filename) => {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);

  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }

  return new File([u8arr], `${filename}.${mime.substring(mime.indexOf('/') + 1, mime.length)}`, { type: mime });
};

/**
 * Converts a Blob to a Base64 encoded string.
 * @param {Blob} blob - The Blob object to convert.
 * @returns {Promise<string>} A promise that resolves with the Base64 encoded string.
 */

export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new Response(blob).arrayBuffer();

    reader
      .then((buffer) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;

        for (let i = 0; i < len; i++) {
          binary += String.fromCharCode(bytes[i]);
        }

        const base64String = btoa(binary);
        resolve(`data:${blob.type};base64,${base64String}`);
      })
      .catch(() => {
        reject(new Error('Failed to convert Blob to Base64'));
      });
  });
};

/**
 * Gets the dimensions (width and height) of an image from a Blob.
 * @param {Blob} blob - The image Blob to get dimensions from.
 * @returns {Promise<{width: number, height: number}>} A promise that resolves with the image dimensions.
 */
export const getImageDimensions = (blob) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);

    img.onload = () => {
      // Clean up the object URL after the image is loaded
      URL.revokeObjectURL(objectUrl);
      resolve({ width: img.width, height: 'auto' });
    };

    img.onerror = (error) => {
      // Clean up the object URL in case of an error
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image from Blob'));
    };

    img.src = objectUrl; // Set the src to the Blob URL
  });
};

/**
 * Gets the width and height of an image file.
 *
 * @param {File} file - The image file to process.
 * @returns {Promise<{ width: number, height: number }>} A promise that resolves to an object containing the width and height of the image.
 */
export const getImageDimensionsFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    // Create a temporary URL for the file
    const url = URL.createObjectURL(file);

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      // Clean up the object URL since we no longer need it
      URL.revokeObjectURL(url);
    };

    img.onerror = () => {
      reject(new Error('Could not load the image.'));
      // Clean up the object URL in case of error as well
      URL.revokeObjectURL(url);
    };

    // Set the src of the image to the temporary URL
    img.src = url;
  });
};

/**
 * @name resizeImage
 *
 * @param {*} file
 * @param {*} newWidth
 * @param {*} newHeight
 * @returns
 */
export const resizeImage = (file, newWidth, newHeight) => {
  return new Promise((resolve) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.src = url;

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, newWidth, newHeight);

      canvas.toBlob((blob) => {
        const resizedFile = new File([blob], file.name, { type: file.type });
        resolve(resizedFile);
        URL.revokeObjectURL(url);
      }, file.type);
    };

    // image.onerror = (error) => {
    //   reject(error);
    //   URL.revokeObjectURL(url);
    // };
  });
};

/**
 * @name validateMessageResponse
 *
 * @param {*} message
 * @param {*} type
 * @param {*} message_replace
 * @returns {{ message: string }}
 */
export const validateMessageResponse = (message, type, message_replace) => {
  return {
    message: isType(message, type) ? message : message_replace,
  };
};

/**
 * Get a list of Japanese governments
 */
export const getJapaneseGovernments = (currentLanguage = ENUM_LANGUAGE_TEXT.JAPANESE) => {
  const japanese_government = currentLanguage === ENUM_LANGUAGE_TEXT.JAPANESE ? japanese_government_jp : japanese_government_vi;
  const governmentGrouped = _.groupBy(japanese_government, 'name');
  const transformedArray = _.map(governmentGrouped, (item, key) => {
    return {
      label: key,
      options: item.map((item) => ({
        code: item.code,
        value: `${item.name}${item.city_name}`,
        label: `${item.name}${item.city_name}`,
      })),
    };
  });

  return transformedArray;
};

/**
 * Get a list of Japanese government names and codes
 *
 * @param {boolean} isName - Whether to get the names or codes. Default: true (names)
 * @returns {Array} - An array of objects containing the government name and code.
 */
export const getCodeJapaneseGovernmentsWith = (name, isName = true, currentLanguage = ENUM_LANGUAGE_TEXT.JAPANESE) => {
  const data = getJapaneseGovernments(currentLanguage);
  let matchingGovernments = '';

  data.forEach((group) => {
    group.options.map((option) => {
      if (isName) {
        if (option.value === name) {
          matchingGovernments = option.code;
        }
      } else {
        if (option.code === name) {
          matchingGovernments = option.value;
        }
      }
    });
  });

  return matchingGovernments;
};

/**
 * Get a Japanese government name by code
 *
 * @param {string} code - The code of the government.
 * @returns {string} - The name of the government.
 */
export const getJapaneseGovernmentName = (code, currentLanguage = ENUM_LANGUAGE_TEXT.JAPANESE) => {
  if (!code) return '';
  const data = currentLanguage === ENUM_LANGUAGE_TEXT.JAPANESE ? japanese_government_jp : japanese_government_vi;
  const government = data.find((item) => item.code === code);
  if (currentLanguage === ENUM_LANGUAGE_TEXT.VIETNAMESE) {
    return government ? `${government.city_name} ${government.name}` : '';
  }
  return government ? `${government.name}${government.city_name}` : '';
};

/**
 * Cleans a survey data object by removing any items with an action property equal to "delete".
 * It recursively traverses the object structure, removing sections and questions as needed.
 *
 * @param {Object|Array} obj - The survey data object or array to clean.
 * @returns {Object|Array|undefined} - The cleaned object or array, or undefined if an item is marked for deletion.
 */
export const cleanDataSurvey = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(cleanDataSurvey).filter((item) => item !== undefined);
  }

  if (typeof obj === 'object' && obj !== null) {
    const newObj = _.cloneDeep(obj);

    if (newObj.action === 'delete') {
      return undefined;
    }

    if (newObj.sections) {
      newObj.sections = cleanDataSurvey(newObj.sections);
    }

    if (newObj.questions) {
      newObj.questions = cleanDataSurvey(newObj.questions);
    }

    return newObj;
  }

  return obj;
};

/**
 * Description placeholder
 *
 * @param {*} input
 * @returns {*}
 */
export const validateJapanese = (input) => {
  const japanesePattern = /[\u3040-\u30FF\u4E00-\u9FFF\uFF61-\uFF9F]/;
  return japanesePattern.test(input);
};

/**
 * Check is full width character
 *
 * @param {*} char
 * @returns
 */
export const isFullWidth = (char) => {
  for (let i = 0; i < char.length; i++) {
    const code = char.charCodeAt(i);
    if ((code >= 0xff01 && code <= 0xff60) || (code >= 0xffe0 && code <= 0xffee)) {
      return true;
    }
  }
  return false;
};

/**
 * Remove Japanese characters
 *
 * @param {*} input
 * @returns
 */
export const removeJapanese = (input) => {
  const japanesePattern = /[\u3040-\u30FF\u4E00-\u9FFF\uFF61-\uFF9F]/g;
  return input.replace(japanesePattern, '');
};

/**
 * Remove full width character
 *
 * @param {*} input
 * @returns
 */
export const removeFullWidth = (input) => {
  let result = '';
  for (let i = 0; i < input.length; i++) {
    const code = input.charCodeAt(i);
    if (!((code >= 0xff01 && code <= 0xff60) || (code >= 0xffe0 && code <= 0xffee))) {
      result += input[i];
    }
  }
  return result;
};

/**
 * None function
 *
 * @returns {boolean}
 */
export const noneFunction = () => {
  return true;
};

/**
 * Render title if choose type
 *
 * @param {*} input
 * @returns
 */
export const renderTitleIfChooseType = (choose_type) => {
  if (choose_type === 'はい') {
    return TEXT_CREATE_SURVEY.TEXT_IF_YOU_CHOOSE.replace('{choose_type}', '「はい」');
  }
  return TEXT_CREATE_SURVEY.TEXT_IF_YOU_CHOOSE.replace('{choose_type}', choose_type);
};

/**
 * Check exists video category
 *
 * @param {*} data
 * @returns
 */
export const checkExistsVideoCategory = (name) => {
  const categoryKey = Object.keys(CATEGORY_VIDEOS_ASE);
  return categoryKey.includes(name);
};

/**
 * Handle image survey
 *
 * @param {*} images
 * @returns
 */

export const handleImageSurvey = async (images) => {
  try {
    const processedImages = [];
    for (const image of images || []) {
      if (image && image.signed_url) {
        const imagesBlob = await fetch(image.signed_url, {
          headers: {
            'x-goog-if-match': image.etag,
          },
        }).then((res) => res.blob());
        const url_preview = URL.createObjectURL(imagesBlob);
        const image_url = await blobToBase64(imagesBlob);
        const dimensions = await getImageDimensions(imagesBlob);

        processedImages.push({
          ...image,
          size: dimensions,
          image_url: image_url,
          url_preview: url_preview,
        });
      } else {
        processedImages.push({});
      }
    }

    return processedImages;
  } catch (error) {
    handleError(error);
    return [];
  }
};

/**
 * Handle video
 *
 * @param {*} videos
 * @returns
 */
export const handleVideoSurvey = async (videos) => {
  try {
    const processedVideos = [];
    for (const video of videos || []) {
      if (video && video.signed_url) {
        const videoBlob = await fetch(video.signed_url, {
          headers: {
            'x-goog-if-match': video.etag,
          },
        }).then((res) => res.blob());
        const url_preview = URL.createObjectURL(videoBlob);
        const video_url = await blobToBase64(videoBlob);

        processedVideos.push({
          ...video,
          video_url: video_url,
          url_preview: url_preview,
        });
      } else {
        processedVideos.push({});
      }
    }

    return processedVideos;
  } catch (error) {
    handleError(error);
    return [];
  }
};

/**
 * Format time
 *
 * @param {*} time
 * @returns
 */
export const formatTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

/**
 * Calculator height contain
 * @description calculate height contain with screen - header - ...
 * @param {Array} lstComponentIds - List component ids
 * @param {Array} lstComponentPaddingIds - List component padding ids
 * @param {Array} lstComponentMarginIds - List component margin ids
 * @returns {string} - Height contain
 */

export const calculatorHeightContain = (lstComponentIds, lstComponentPaddingIds, lstComponentMarginIds, minus_extra = 0) => {
  const height_padding =
    lstComponentPaddingIds?.reduce((acc, componentId) => {
      const component = document.getElementById(componentId.id);
      const style = window.getComputedStyle(component);
      const paddingTop = style.paddingTop || 0;
      const paddingBottom = style.paddingBottom || 0;
      const paddingTotal = (Number(paddingTop.replace('px', '')) + Number(paddingBottom.replace('px', ''))) * (componentId.percentage || 1);
      return acc + paddingTotal;
    }, 0) || 0;

  const height_margin =
    lstComponentMarginIds?.reduce((acc, componentId) => {
      const component = document.getElementById(componentId.id);
      const style = window.getComputedStyle(component);
      const marginTop = style.marginTop || 0;
      const marginBottom = style.marginBottom || 0;
      const marginTotal = (Number(marginTop.replace('px', '')) + Number(marginBottom.replace('px', ''))) * (componentId.percentage || 1);
      return acc + marginTotal;
    }, 0) || 0;

  const height_without_padding_and_margin =
    lstComponentIds?.reduce((acc, componentId) => {
      const component = document.getElementById(componentId.id);
      const style = window.getComputedStyle(component);
      const height = style.height || 0;
      const heightTotal = Number(height.replace('px', '')) * (componentId.percentage || 1);
      return acc + heightTotal;
    }, 0) || 0;

  return `calc(100vh - var(--header-height) - ${height_padding + height_margin + height_without_padding_and_margin + minus_extra}px)`;
};

/**
 * Check if an element is in view
 *
 * @param {*} el
 * @returns
 */
export const isElementInView = (el) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
};

/**
 * Get the scrollable parent of an element
 *
 * @param {*} el
 * @returns
 */
export const getScrollableParent = (el) => {
  if (!el) return null;
  let parent = el.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;
    const isScrollableY = (overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight;
    const isScrollableX = (overflowX === 'auto' || overflowX === 'scroll') && parent.scrollWidth > parent.clientWidth;
    if (isScrollableY || isScrollableX) return parent;
    parent = parent.parentElement;
  }
  return null;
};
/**
 * Get timezone offset string
 *
 * @returns {string}
 */
export const getTimeZoneOffsetString = () => {
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const minutes = String(absOffset % 60).padStart(2, '0');
  return `${sign}${hours}${minutes}`;
};

/**
 * Validate time setting
 *
 * @param {*} date
 * @returns {Array}
 */
export const validateTimeSetting = (settingStartTime, settingEndTime) => {
  const isEqual = settingEndTime?.value?.isSame(settingStartTime?.value);
  const isBefore = settingEndTime?.value?.isBefore(settingStartTime?.value);
  if (settingEndTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET && (isBefore || isEqual)) {
    return false;
  }
  return true;
};

/**
 * Validate before now
 *
 * @param {*} settingStartTime
 * @param {*} settingEndTime
 * @returns {boolean}
 */
export const validateBeforeNow = (settingStartTime, settingEndTime) => {
  const now = dayjs();
  const startBeforeNow = settingStartTime?.value?.isBefore(now);
  const endBeforeNow = settingEndTime?.value?.isBefore(now);

  if (settingStartTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.START_TIME_SET && startBeforeNow) {
    return false;
  }
  if (settingEndTime?.type === ENUM_TYPE_PUBLIC_PERIOD_SETTING.END_TIME_SET && endBeforeNow) {
    return false;
  }
  return true;
};

/**
 * Handle motor skills
 *
 * @param {*} status
 * @returns
 */
export const handleMotorSkills = (status) => {
  switch (status) {
    case 1:
      return <img src={AthleticAbilityIcon_1} alt="athletic-ability-icon-1" />;
    case 2:
      return <img src={AthleticAbilityIcon_2} alt="athletic-ability-icon-2" />;
    case 3:
      return <img src={AthleticAbilityIcon_3} alt="athletic-ability-icon-3" />;
    case 4:
      return <img src={AthleticAbilityIcon_4} alt="athletic-ability-icon-4" />;
    default:
      return null;
  }
};

/**
 * Handle check language
 *
 * @param {*} lang
 * @returns
 */
export const handleCheckLanguage = (municipality) => {
  if (municipality.toString() === '999999' || municipality.toString() === '000000') {
    return 'vi';
  }
  return 'ja';
};

/**
 * Get duration video from video url
 *
 * @param {*} videoUrl
 * @returns
 */
export const getVideoDurationFromVideoUrl = (videoUrl) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = function () {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = function (e) {
      reject(new Error('Cannot load video metadata.'));
    };

    video.src = videoUrl;
  });
};

/**
 * Get duration video from file
 *
 * @param {*} file
 * @returns
 */
export const getVideoDurationFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = function () {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = function (e) {
      reject(new Error('Cannot load video metadata.'));
    };

    video.src = URL.createObjectURL(file);

    video.load();
  });
};

/**
 * Convert url to file
 *
 * @param {*} url
 * @param {*} filename
 * @param {*} mimeType
 * @returns
 */
export const urlToFile = async (url, filename, mimeType) => {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType || blob.type });
};

/**
 * Get filename from url
 *
 * @param {*} url
 * @returns
 */
export const getFileName = (url) => url.split('/').pop();

/**
 * Validate filename
 *
 * Description: Validate filename to disallow specific characters: \ / : * ? " ' < > `
 *
 * @param {*} filename
 * @returns
 */
export const validateFileName = (filename) => {
  const forbiddenChars = /[\\/:*?"'<>`]/;
  return !forbiddenChars.test(filename);
};

/**
 * Get file extension
 *
 * @param {*} filename
 * @returns
 */
export const getFileExtension = (filename) => {
  return filename.split('.').pop();
};

/**
 * Generate random file name
 *
 * @param {*} index
 * @param {*} type
 * @returns
 */
export const generateRandomFileName = (index = 0, type = 'video') => {
  return `${moment().unix()}${Math.floor(Math.random() * (9999 - 1111 + 1)) + 1111}${index}.${type}`;
};

/**
 * To extended file
 *
 * @param {*} file
 * @param {*} duration
 * @returns
 */
export const toExtendedFile = (file, duration = 0) => {
  if (file.type === 'video/mp4') {
    return Object.assign(file, {
      duration,
    });
  }
  return Object.assign(file);
};

/**
 * Format date
 *
 * @param {*} date
 * @returns
 */
export const formatDate = (date) => {
  const now = dayjs();
  const diffDay = now.startOf('day').diff(dayjs(date).startOf('day'), 'day');
  const diffSecond = dayjs(now).diff(dayjs(date), 'second');

  if (diffDay >= 2) {
    return dayjs(date).format('YYYY/MM/DD');
  }

  if (diffDay >= 1) {
    return 'yesterday';
  }

  if (diffSecond < 60) return 'Now';

  if (diffSecond < 3600) {
    const mins = Math.floor(diffSecond / 60);
    return `${mins}min`;
  }

  if (diffSecond < 86400) {
    const hours = Math.floor(diffSecond / 3600);
    return `${hours}hr${hours > 1 ? 's' : ''}`;
  }
};

/**
 * Get age from month and year
 *
 * @param {number} month - The month of the birth date.
 * @param {number} year - The year of the birth date.
 * @returns {string} - The age.
 */
export const getAgeFromMonthAndYear = (birthMonth, birthYear, t = null, currentLanguage = ENUM_LANGUAGE_TEXT.JAPANESE) => {
  if (!birthMonth || !birthYear) return '';

  if (!t) {
    return {
      name: '',
      class: '',
    };
  }

  const birthDate = moment({ year: birthYear, month: birthMonth - 1 });
  const today = moment();

  let years = today.diff(birthDate, 'years');
  let months = today.diff(birthDate.clone().add(years, 'years'), 'months');

  const space = currentLanguage === ENUM_LANGUAGE_TEXT.JAPANESE ? '' : ' ';

  if (years === 0) {
    return `${months}${space}${t('MONTHS')}`;
  } else {
    return `${years}${space}${t('YEARS')}${space}${months}${space}${t('MONTHS')}`;
  }
};

/**
 * Get gender text
 *
 * @param {*} gender
 * @returns
 */
export const getGenderText = (gender_text_jp, t = null) => {
  if (!t) {
    return gender_text_jp;
  }
  return gender_text_jp === ENUM_GENDER_JP_TEXT.MALE ? t('MALE') : t('FEMALE');
};

/**
 * Scroll to bottom by ref
 *
 * @param {*} ref
 * @returns
 */
export const scrollToBottomByRef = (ref) => {
  if (ref.current) {
    ref.current.scrollTo({
      top: ref.current.scrollHeight,
    });
  }
};

/**
 * Scroll to bottom by id
 *
 * @param {*} id
 * @returns
 */
export const scrollToById = (id, type = 'bottom') => {
  const element = document.getElementById(id);
  if (element) {
    if (type === 'bottom') {
      element.scrollTo({
        top: element.scrollHeight,
      });
    } else {
      element.scrollTo({
        top: 0,
      });
    }
  }
};

/**
 * Group message by date
 *
 * @param {*} data
 * @returns
 */
export const groupMessageDate = (data, oldData = {}) => {
  const grouped = { ...oldData };

  data.forEach((chat) => {
    const datePart = dayjs(chat.created_at).format(FORMAT_TIME.FULL_DATE_TIME).split(' ')[0];

    if (!grouped[datePart]) {
      grouped[datePart] = [];
    }

    grouped[datePart].push(chat);
  });

  // Sort messages in each group by time
  Object.keys(grouped).forEach((date) => {
    grouped[date].sort((a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf());
  });

  const sortedKeys = Object.keys(grouped).sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf());
  const sortedGrouped = {};
  sortedKeys.forEach((key) => {
    sortedGrouped[key] = grouped[key];
  });

  return sortedGrouped;
};

/**
 * Get file sample
 *
 * @param {*} category_id
 * @returns
 */
export const getFileSample = (fileSamples) => {
  return {
    fileSamples,
    hasVideo: (fileSamples?.file_samples?.filter((file) => file?.file_type === 'video')?.length || 0) > 0,
    hasImage: (fileSamples?.file_samples?.filter((file) => file?.file_type === 'image')?.length || 0) > 0,
    videoLength: fileSamples?.file_samples?.filter((file) => file?.file_type === 'video')?.length || 0,
    imageLength: fileSamples?.file_samples?.filter((file) => file?.file_type === 'image')?.length || 0,
    fileLength: fileSamples?.file_samples?.length || 0,
  };
};

/**
 * Get minutes and seconds
 *
 * @param {*} duration
 * @returns
 */
export const getMinutesAndSeconds = (duration) => {
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;
  return {
    minutes,
    seconds,
  };
};

/**
 * Calculate width
 *
 * @param {*} sizeDefault
 * @param {*} widthWindowDefault
 * @param {*} currentWidth
 * @returns
 */
export const calculateWidth = (sizeDefault, widthWindowDefault = 1920, currentWidth) => {
  const ratio = widthWindowDefault / sizeDefault;

  const width = currentWidth / ratio;
  const minWidth = currentWidth / ratio;
  const maxWidth = currentWidth / ratio;

  return {
    width: `${width}px`,
    minWidth: `${minWidth}px`,
    maxWidth: `${maxWidth}px`,
  };
};

/**
 * Convert SVG to Base64
 *
 * @param {*} svg
 * @returns
 */
export const convertSvgToBase64 = async (svgPath) => {
  try {
    const response = await fetch(svgPath);
    const svgText = await response.text();

    const base64 = btoa(svgText);
    return `data:image/svg+xml;base64,${base64}`;
  } catch {
    return null;
  }
};
